import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Instagram, Youtube, Twitter, Heart } from "lucide-react";

const INQUIRY_TYPES = ["General", "Volunteer", "Partnership", "Media", "A Better Ethiopia", "Mercy Made", "Other"];
const field = "w-full border border-[#1F4D2E]/15 bg-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C1272D]";

export default function ContactPage() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", inquiry_type: "General", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [news, setNews] = useState({ name: "", email: "" });
  const [newsBusy, setNewsBusy] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contacts", form);
      toast.success("Thank you — your message has been sent!");
      setForm({ name: "", email: "", phone: "", inquiry_type: "General", subject: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setNewsBusy(true);
    try {
      const { data } = await api.post("/newsletter", news);
      toast.success(data.message || "Subscribed!");
      setNews({ name: "", email: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong"); }
    finally { setNewsBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Get in touch</p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-[#1F4D2E] leading-[0.95]">Let's stay connected.</h1>
          <p className="text-lg text-[#4F6B54] mt-6 leading-relaxed">Have a question, want to volunteer, or interested in partnering with us? Send us a message and we'll get back to you.</p>

          {settings.contact_email && (
            <a href={`mailto:${settings.contact_email}`} className="mt-8 inline-flex items-center gap-3 text-[#1F4D2E] font-semibold" data-testid="contact-email"><span className="w-11 h-11 rounded-full bg-[#E7EDE2] flex items-center justify-center"><Mail size={18} /></span>{settings.contact_email}</a>
          )}

          <div className="flex items-center gap-3 mt-8">
            {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center text-[#1F4D2E] hover:bg-[#C1272D] hover:text-white hover:border-[#C1272D] transition-colors"><Instagram size={18} /></a>}
            {settings.social_youtube && <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center text-[#1F4D2E] hover:bg-[#C1272D] hover:text-white hover:border-[#C1272D] transition-colors"><Youtube size={18} /></a>}
            {settings.social_x && <a href={settings.social_x} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center text-[#1F4D2E] hover:bg-[#C1272D] hover:text-white hover:border-[#C1272D] transition-colors"><Twitter size={18} /></a>}
            {settings.gofundme_url && <a href={settings.gofundme_url} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center text-[#1F4D2E] hover:bg-[#C1272D] hover:text-white hover:border-[#C1272D] transition-colors"><Heart size={18} /></a>}
          </div>

          <form onSubmit={subscribe} className="mt-10 bg-[#E7EDE2] rounded-xl p-6" data-testid="newsletter-form">
            <h3 className="font-display text-lg font-semibold text-[#1F4D2E] mb-3">Get honest updates</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <input required type="email" value={news.email} onChange={(e) => setNews({ ...news, email: e.target.value })} data-testid="newsletter-email" className={`${field} flex-1`} placeholder="you@email.com" />
              <button disabled={newsBusy} data-testid="newsletter-submit" className="bg-[#1F4D2E] text-white rounded-lg px-5 py-3 font-semibold hover:bg-[#163a22] transition-colors whitespace-nowrap">{newsBusy ? "…" : "Subscribe"}</button>
            </div>
          </form>
        </div>

        <div className="md:col-span-7">
          <form onSubmit={submit} className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-8 md:p-10" data-testid="contact-form">
            <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-6">Send us a message</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#1F4D2E] mb-1.5">Name *</label>
                <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" className={field} placeholder="Your name" /></div>
              <div><label className="block text-sm font-medium text-[#1F4D2E] mb-1.5">Email *</label>
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" className={field} placeholder="you@email.com" /></div>
              <div><label className="block text-sm font-medium text-[#1F4D2E] mb-1.5">Phone / WhatsApp</label>
                <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" className={field} placeholder="Optional" /></div>
              <div><label className="block text-sm font-medium text-[#1F4D2E] mb-1.5">Inquiry type</label>
                <select value={form.inquiry_type} onChange={(e) => set("inquiry_type", e.target.value)} data-testid="contact-type" className={field}>
                  {INQUIRY_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select></div>
            </div>
            <label className="block text-sm font-medium text-[#1F4D2E] mb-1.5 mt-4">Subject</label>
            <input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" className={field} placeholder="How can we help?" />
            <label className="block text-sm font-medium text-[#1F4D2E] mb-1.5 mt-4">Message *</label>
            <textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" className={field} placeholder="Write your message…" />
            <button disabled={busy} data-testid="contact-submit" className="mt-6 w-full bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#9E1F24] transition-colors inline-flex items-center justify-center gap-2">{busy ? "Sending…" : <>Send message <Send size={17} /></>}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
